<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { getUsersAction } from '@/api/functions/users/get-users.action'
import UserCard from './UserCard.vue'

const isOpen = ref(false)

const currentPage = ref(1)

const pendingStatuses = [
  'EMAIL_VERIFICATION_PENDING',
  'MOBILE_REGISTRATION_PENDING',
  'MOBILE_VERIFICATION_PENDING',
  'NATIONAL_CODE_REGISTRATION_PENDING',
  'NATIONAL_CODE_VERIFICATION_PENDING',
  'IDENTIFICATION_REGISTRATION_PENDING',
  'IDENTIFICATION_VERIFICATION_PENDING',
]

const {
  data: pendingUsersData,
  isLoading: isPendingLoading,
  refetch: refetchPendingUsers,
} = useQuery({
  queryKey: computed(() => ['pending-users', currentPage.value]),
  queryFn: () =>
    getUsersAction({
      page: currentPage.value,
      status: pendingStatuses.join(','),
    }),
  enabled: false,
  // keepPreviousData: true,

  staleTime: 1000 * 60 * 3,
})

watch(isOpen, (val) => {
  if (val) refetchPendingUsers()
})

const users = computed(() => pendingUsersData.value?.data || [])
const pagination = computed(() => pendingUsersData.value?.pagination)
const totalItems = computed(() => pagination.value?.total || 0)

const goToPage = (page: number) => {
  currentPage.value = page
  refetchPendingUsers()
}
</script>

<template>
    <!-- PENDING USERS SECTION -->
    <div class="w-full rounded my-6 shadow-xl">
        <div @click="isOpen = !isOpen"
            class="w-full cursor-pointer h-[100px] px-6 py-4 bg-white text-[#1b325a] font-medium text-[13.125px] rounded-xl flex items-center justify-between">
            <span>کاربران در انتظار تایید مدارک هویتی</span>
            <div class="flex items-center gap-1.5">
                مشاهده
                <img src="/svg/chevron-down.svg" alt="chevron"
                    class="w-6 h-6 pointer-events-none transition-transform duration-200"
                    :class="{ 'rotate-180': isOpen }" />
            </div>
        </div>

        <!-- Users List -->
        <div v-if="isOpen" class="mt-4 flex flex-col gap-4 px-6 pb-6">
            <div v-if="isPendingLoading" class="text-center py-6 text-gray-500">
                در حال دریافت کاربران در انتظار تایید...
            </div>

            <template v-else>
                <UserCard v-for="(user, index) in users" :key="`pending-user-${index}`" :user="user"
                    @confirmUser="refetchPendingUsers" />

                <Pagination v-if="users.length" v-model:currentPage="currentPage" :itemsPerPage="pagination?.perPage"
                    :totalItems="totalItems" @update:currentPage="goToPage" :isAdmin="true" />

                <div v-if="!isPendingLoading && users.length === 0" class="text-center py-4 text-gray-400">
                    کاربری در انتظار تأیید یافت نشد.
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped></style>
